import React, { useState, useEffect, useContext, useRef } from 'react';
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Select from 'react-select';
import * as dayjs from 'dayjs'
import { useTranslation } from 'react-i18next';
import { postContentWithAction, putContentWithAction, getContentWithAction } from '../../apiConnect';
import { getSelectedFarmId, getSelectedLang, getSelectedYear, getSelectedPlot, basicStyles } from '../../common';
import { formatPlannedDate, groupByKey, valueMax, dayJsFormatDate, groupByMarked, round, updateCalculativeArray, UpdateHarvestLoad } from '../../commonFunctions';
import NumberFormat from 'react-number-format';
import CalendarModal from './CalendarModal';
import Spinner from 'react-bootstrap/Spinner';
import { useAlert } from 'react-alert'
import AppContext from '../../AppContext';
import { ICONS } from '../../img/icons'
import Icon from '../../img/Icon'
import { OverlayTrigger, Tooltip, Badge } from 'react-bootstrap'

import * as harvestConstants from '../../Constants/HarvestConstants.js';

import '../../css/general.css'


const MarkHarvestModal = (props) => {
    const { t } = useTranslation();
    const { auth } = useContext(AppContext);

    const [plannedHarvests, setPlannedHarvests] = useState([]);
    const [combinedHarvests, setCombinedHarvests] = useState([]);
    const [calendarShow, setCalendarShow] = useState(false);
    const [date, setDate] = useState(dayjs().format());
    const [contentIsLoading, setContentIsLoading] = useState(true);
    const [infoText, setInfoText] = useState('Ladataan tietoja');
    const [saveInProgress, setSaveInProgress] = useState(false);

    const [refreshComponents, setRefreshComponents] = useState(null);

    //For area and amount calculations, CCD = CalculationCombinedData
    const [cCD, setCCD] = useState([]);

    const alert = useAlert();
    let pesticideWithdrawal = useRef(null);

    /* TODO: Move this to constants and check their IDs */
    const allowIdsWithUnit = useRef([21, 22, 42, 51]);
    const harvestUnitsGroup = [
        { name: t('UnitHarvestingSmallBale'), value: harvestConstants.SMALL_BALE_UNIT },
        { name: t('UnitHarvestingRoundBale'), value: harvestConstants.ROUND_BALE_UNIT },
        { name: t('UnitHarvestingCubicBale'), value: harvestConstants.CUBIC_BALE_UNIT },
        { name: t('UnitHarvestingWagon'), value: harvestConstants.WAGON_UNIT }
    ]

    useEffect(() => {
        var plotIds = [];

        props.selectEvent.selectedPlots.forEach(p => {
            plotIds.push({ plotId: p.apNumber });
        });

        const promiseHarv = new Promise((resolve, reject) => {
            postContentWithAction(auth.getAccessToken(), 'harvest', 'harvestsForPlots', {
                models: plotIds, lang: getSelectedLang()
            }, (data) => {
                resolve(data);
            }).catch(e => {
                reject(Error("Promise rejected"));
            });
        });

        const storagesPromise = new Promise((resolve, reject) => {
            getContentWithAction(auth.getAccessToken(), 'resource', 'storages', {}).then((data) => {
                resolve(data);
            }).catch(e => {
                reject(Error("Promise rejected"));
            });
        });

        var farmId = getSelectedFarmId();
        var farmYear = getSelectedYear();
        /* Get assignments if not in props.
         * Used to find existing assignments for modifications if not updating
         * from the assignment modal.
         */
        const assignmentsPromise = new Promise((resolve, reject) => {
            // Check if assignments are already in props --> no need to fetch again
            if (props.assignments) {
                resolve(null);
            }
            else {
                getContentWithAction(auth.getAccessToken(), 'assignments', 'getAssignments', { farmId, farmYear }).then((data) => {
                    resolve(data);
                }).catch(e => {
                    reject(Error("Promise rejected"));
                });
            }
        });

        Promise.all([promiseHarv, assignmentsPromise, storagesPromise]).then(([harvests, assignments, storages]) => {
            // If props.assigments is not null, we are in the assignments only "mode" and 
            // filter only the harvests that have assignments
            if (props.assignments) {
                harvests = harvests.filter(h => props.assignments.find(a => a.operNo === h.harvestId))
            }
            else {
                harvests = harvests.filter((h) => h.beginDate || h.plannedDate);
            }

            harvests.forEach(h => {
                h.plotData = props.selectEvent.selectedPlots.find(p => p.apNumber === h.plotId);
                h.assignment = props.assignments ? props.assignments.find(a => a.operNo === h.harvestId) : assignments.find(a => a.operNo === h.harvestId);
                // Only combine harvests when bale/wagon units are not used
                h.groupCheck = h.loadAmount ? h.product + '_' + h.unitNumber + '_' + h.plotId : h.product + '_' + + h.amount + '_' + h.storageId;
            });
            setPlannedHarvests(harvests);

            var grouped1 = groupByKey(harvests, 'groupCheck');
            var grouped2 = [];
            for (const key in grouped1) {
                var m = groupByMarked(grouped1[key], 'beginDate', 'plannedDate');

                for (const type in m) {
                    for (const key in m[type]) { // Looping grouped keys
                        let combinedIds = [];
                        let combinedArea = 0;
                        let combinedAmount = { min: m[type][key][0].amount, max: m[type][key][0].amount };
                        let combinedMateria = { min: m[type][key][0].dryMateriaContent, max: m[type][key][0].dryMateriaContent };
                        let combinedVolume = { min: m[type][key][0].volume, max: m[type][key][0].volume };
                        let combinedVolumeWeight = { min: m[type][key][0].volumeWeight, max: m[type][key][0].volumeWeight };

                        m[type][key].forEach(s => { // Looping ferts
                            combinedIds.push({
                                plotId: s.plotId,
                                harvestId: s.harvestId,
                                unitNumber: s.unitNumber,
                                plotName: s.plotData.name + ' - ' + s.plotData.apChar,
                                plotArea: s.plotData.area
                            });
                            if (s.area)
                                combinedArea += s.area;
                            else
                                combinedArea += s.plotData.area;

                            combinedAmount.min = Math.min(combinedAmount.min, s.amount);
                            combinedAmount.max = Math.max(combinedAmount.max, s.amount);

                            combinedMateria.min = Math.min(combinedMateria.min, s.dryMateriaContent);
                            combinedMateria.max = Math.max(combinedMateria.max, s.dryMateriaContent);

                            combinedVolume.min = Math.min(combinedVolume.min, s.volume);
                            combinedVolume.max = Math.max(combinedVolume.max, s.volume);

                            combinedVolumeWeight.min = Math.min(combinedVolumeWeight.min, s.volumeWeight);
                            combinedVolumeWeight.max = Math.max(combinedVolumeWeight.max, s.volumeWeight);
                        });
                        let combinedData = { ...m[type][key][0] }; // Copying first element for base data

                        if (combinedAmount.min !== combinedAmount.max) combinedData.combinedAmount = null;
                        if (combinedMateria.min !== combinedMateria.max) combinedData.combinedMateria = null;
                        if (combinedVolume.min !== combinedVolume.max) combinedData.combinedVolume = null;
                        if (combinedVolumeWeight.min !== combinedVolumeWeight.max) combinedData.combinedVolumeWeight = null;

                        var filteredStorages = storages.filter(s => s.product === combinedData.product.toString()).map(x => { return { ...x } });

                        var harvestObject = {
                            date: key,
                            storages: filteredStorages,
                            combinedData: combinedData,
                            combinedArea: combinedArea,
                            combinedIds: combinedIds,
                            combinedAmount: combinedAmount,
                            combinedMateria: combinedMateria,
                            combinedVolume: combinedVolume,
                            combinedVolumeWeight: combinedVolumeWeight,
                            unitNumber: combinedIds[0].unitNumber
                        };

                        grouped2.push({
                            harvest: key,
                            productName: combinedData.productName,
                            plannedDate: combinedData.plannedDate,
                            date: combinedData.beginDate,
                            objects: [harvestObject]
                        });
                    }
                }
            }

            // First sort all planned pesticides by date, then by name
            var sortedHarvests = grouped2.filter(s => s.plannedDate && !s.date).sort((a, b) => {
                if (a.plannedDate < b.plannedDate) return -1;
                if (a.plannedDate > b.plannedDate) return 1;
                if (a.productName < b.productName) return -1;
                if (a.productName > b.productName) return 1;
                return 0;
            });

            // Then sort all done pesticides by name and append to the end of the list
            sortedHarvests.push(...grouped2.filter(f => f.date).sort((a, b) => {
                if (a.productName < b.productName) return -1;
                if (a.productName > b.productName) return 1;
                if (a.date < b.date) return -1;
                if (a.date > b.date) return 1;
                return 0;
            }));

            setCCD(sortedHarvests);
            setCombinedHarvests(sortedHarvests);

            setContentIsLoading(false);
            setInfoText('Ei suunniteltuja sadonkorjuita');
        }).catch(error => console.log(`Error in executing ${error}`));

        retrievePesticideWithdrawal();

    }, [props.selectEvent]);

    const retrievePesticideWithdrawal = () => {
        var plots = props.selectEvent.selectedPlots.map(p => p.apNumber);
        const wdCallback = (data) => {
            pesticideWithdrawal.current = data;
            onDateChange(dayjs(date).format());
        };

        postContentWithAction(auth.getAccessToken(), 'resource', 'pesticideWithdrawals', { models: plots }, wdCallback);
    }

    const isPlotWithdrawalPeriod = (plotId, newdate) => {
        if (pesticideWithdrawal.current?.length > 0) {
            var plotWithdrawalDate = pesticideWithdrawal.current.find(wd => wd.plotId === plotId)?.withdrawalDate;
            if (plotWithdrawalDate && dayjs(newdate).isBefore(dayjs(plotWithdrawalDate))) {
                return { withdrawalDate: plotWithdrawalDate, isWithdrawalPeriod: true };
            }
        }
        return { withdrawalDate: null, isWithdrawalPeriod: false };
    }

    const onDateChange = (newdate) => {
        const alertText = (p, wdDate) => t('WithdrawalWarning').replace("{0}", p.name + " - " + p.apChar).replace("{1}", wdDate);

        props.selectEvent.selectedPlots.forEach(p => {
            let isWdPeriod = isPlotWithdrawalPeriod(p.apNumber, newdate);
            if (isWdPeriod.isWithdrawalPeriod) {
                let wdDate = dayjs(isWdPeriod.withdrawalDate).format("DD.MM.YYYY");
                alert.show(alertText(p, wdDate), { type: 'info' });
            }
        });
    }

    const updateDate = (value) => {
        var newDate = date;
        newDate.beginDate = value;
        setDate(newDate);
        onDateChange(value);
    };

    const saveHarvests = () => {
        setSaveInProgress(true);
        let today = dayjs().format('YYYYMMDD');
        let invalidDate = false;
        var updateHarvests = [];

        combinedHarvests.forEach(product => {
            product.objects.forEach(harv => {
                harv.combinedIds.forEach(plot => {
                    var originalData = plannedHarvests.find(s => s.harvestId === plot.harvestId);
                    var data = harv.combinedData;
                    let formattedDate = dayjs(data.beginDate).format('YYYYMMDD');
                    if (data.beginDate && today < formattedDate) invalidDate = true; // Check for valid dates
                    if (data.beginDate) {
                        updateHarvests.push({
                            plannedDate: originalData.plannedDate,
                            product: data.product,
                            amount: data.amount ? data.amount : originalData.amount,
                            volume: data.volume ? data.volume : originalData.volume,
                            volumeWeight: data.volumeWeight ? data.volumeWeight : originalData.volumeWeight,
                            dryMateriaContent: data.dryMateriaContent ? data.dryMateriaContent : originalData.dryMateriaContent,
                            loadAmount: data.loadAmount,
                            baleWeight: data.baleWeight,
                            description: data.description,
                            storageId: data.storageId,
                            harvestedUnit: data.harvestedUnit,
                            harvestId: plot.harvestId,
                            plotId: plot.plotId,
                            beginDate: data.beginDate,
                            area: originalData.area ? originalData.area : plot.plotArea,
                            starchPercent: originalData.starchPercent ? originalData.starchPercent : 0,
                            assignmentId: originalData.assignment ? originalData.assignment.id : 0
                        });
                    }
                });
            });
        });

        if (!invalidDate) {
            putContentWithAction(auth.getAccessToken(), 'harvest', 'update', {
                models: updateHarvests
            }).then(() => {
                setSaveInProgress(false);
                props.setRefresh(Date.now());
                alert.show(t('SavedSuccesfully'), { type: 'success' });
            }).catch(e => {
                setSaveInProgress(false);
                alert.show(t('SavedFailed'), { type: 'error' });
            });

            props.onHide();
        }
        else {
            alert('invalid date')
        }
    }

    let checkSelectedStorage = (selectedStorage, markRows, currentRow) => {
        markRows.forEach((row) => {
            var sameStorage = row.objects.find(o => o.combinedData.storageId === selectedStorage.id);
            if (!selectedStorage.product && sameStorage.combinedData.product !== currentRow.combinedData.product) {
                alert.show(t('StorageAlreadySelected'), { type: 'error' });
                currentRow.combinedData.storageId = null;
            }
        });
    }

    let getProductUnit = (unit) => {
        var group = harvestUnitsGroup.find(g => g.value === unit);
        return group ? ' - ' + group.name : '';
    }

    return (
        <>
            <Modal show={props.show} onHide={props.onHide}>
                <Modal.Header closeButton>
                    <Modal.Title className="text-dark">{t('MarkHarvestDone')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-dark">
                    <Form>
                        {contentIsLoading ? <Spinner as="span" className="center" animation="border" role="status" variant="secondary" /> : ''}
                        {(combinedHarvests) && (combinedHarvests.length > 0) ? combinedHarvests.map((product, i) =>
                            product.objects.map((harv, j) =>
                                <div key={i} id={i} className="mark-row-style border-bottom">
                                    <div className="mark-row-header">
                                        <b>{harv.combinedData.productName ? harv.combinedData.productName : harv.combinedData.description} {getProductUnit(harv.combinedData.harvestedUnit)}</b>
                                        <br />
                                        {harv.combinedIds.length > 1 ?
                                            <OverlayTrigger trigger="click" rootClose placement="right" overlay={
                                                <Tooltip id="overlay">
                                                    {harv.combinedIds.slice(1).map((p, i) =>
                                                        <div key={p.plotName}>{p.plotName}</div>
                                                    )}
                                                </Tooltip>
                                            }>
                                                <Button size="sm" variant="light">
                                                    {harv.combinedIds[0].plotName} <b>+</b> <Badge pill bg="secondary">{harv.combinedIds.length - 1}</Badge>
                                                </Button>
                                            </OverlayTrigger>
                                            :
                                            harv.combinedIds.length === 1 ?
                                                <Button size="sm" variant="light" className="disabledButton">
                                                    {harv.combinedIds[0].plotName}
                                                </Button>
                                                : ''}
                                        <span className="harvest-combined-area">({harv.combinedArea.toFixed(2)} ha)</span>
                                    </div>


                                    { /* NEW SECTION */ }
                                    <div className="mark-row-content">

                                        {harv.combinedData.loadAmount && harv.unitNumber === harvestConstants.DRY_CONTENT_UNIT_TYPE ?
                                            <div>
                                                <Form.Group>
                                                    <Form.Label className="text-dark">{t('UnitPcsArea')}</Form.Label>
                                                    <NumberFormat
                                                        className="form-control inline-form-field"
                                                        value={cCD[i].objects[j].combinedData.loadAmount}
                                                        decimalScale={0}
                                                        fixedDecimalScale={false}
                                                        placeholder={`${harv.combinedData.loadAmount}`}
                                                        onValueChange={(value) => {
                                                            if (value.floatValue && round(value.floatValue) !== round(cCD[i].objects[j].combinedData.loadAmount)) {
                                                                if (value.floatValue) {
                                                                    var isWagon = harv.unitNumber === harvestConstants.WAGON_UNIT;
                                                                    setCCD(UpdateHarvestLoad(cCD, value.floatValue, i, j, isWagon));
                                                                    harv.combinedData.loadAmount = value.floatValue;
                                                                }
                                                            }
                                                        }}
                                                        displayType={'input'}
                                                        thousandSeparator={' '}
                                                        isAllowed={valueMax}
                                                        allowNegative={false}
                                                        allowedDecimalSeparators={['.', ',']} />
                                                </Form.Group>
                                                <Form.Group controlId="formAmount">
                                                    <Form.Label className="text-dark">{t('Total')}</Form.Label>
                                                    <span className="text-dark inline-form-field harvest-unit-sum">
                                                        <b>
                                                            {harv.unitNumber === harvestConstants.WAGON_UNIT ?
                                                                <>
                                                                    {(harv.combinedData.trailerVolume * harv.combinedData.volumeWeight * harv.combinedData.loadAmount)} kg
                                                                </>
                                                                :
                                                                <>
                                                                    {(harv.combinedData.baleWeight * harv.combinedData.loadAmount)} kg
                                                                </>}
                                                        </b>
                                                        <span> (à: {harv.combinedData.baleWeight} kg)</span>
                                                    </span>
                                                </Form.Group>
                                            </div>
                                            : ''}


                                        {/* Dry Materia products */}
                                        {/* 
                                        {harv.unitNumber === harvestConstants.DRY_CONTENT_UNIT_TYPE ?
                                            <Form.Group controlId="formAmount">
                                                <Form.Label className="text-dark">{t('DryMateriaContent')}</Form.Label>
                                                <NumberFormat
                                                    className="form-control inline-form-field"
                                                    value={cCD[i].objects[j].combinedData.dryMateriaContent}
                                                    decimalScale={2}
                                                    fixedDecimalScale={false}
                                                    placeholder={`${harv.combinedMateria.min} - ${harv.combinedMateria.max}`}
                                                    onValueChange={(value) => {
                                                        if (value.floatValue && round(value.floatValue) !== round(cCD[i].objects[j].combinedData.dryMateriaContent)) {
                                                            if (value.floatValue) {
                                                                setCCD(updateCalculativeArray(cCD, (value.floatValue / cCD[i].objects[j].combinedArea), i, j));
                                                                harv.combinedData.dryMateriaContent = value.floatValue;
                                                            }
                                                        }
                                                    }}
                                                    displayType={'input'}
                                                    thousandSeparator={' '}
                                                    suffix={'  ' + harv.combinedData.unitText}
                                                    isAllowed={valueMax}
                                                    allowNegative={false}
                                                    allowedDecimalSeparators={['.', ',']} />
                                            </Form.Group>
                                            : ''}
                                        */}

                                        {/* Amount Ha */}
                                        <Form.Group controlId="formAmount">
                                            <Form.Label className="text-dark">{t('Amount_ha')}</Form.Label>
                                            <NumberFormat
                                                className="form-control inline-form-field"
                                                value={cCD[i].objects[j].combinedData.amount}
                                                disabled={harv.combinedData.loadAmount && harv.unitNumber === harvestConstants.DRY_CONTENT_UNIT_TYPE}
                                                decimalScale={2}
                                                fixedDecimalScale={false}
                                                placeholder={`${harv.combinedAmount.min} - ${harv.combinedAmount.max}`}
                                                onValueChange={(value) => {
                                                    if (value.floatValue && round(value.floatValue) !== round(cCD[i].objects[j].combinedData.amount)) {
                                                        if (value.floatValue) {
                                                            setCCD(updateCalculativeArray(cCD, value.floatValue, i, j));
                                                            harv.combinedData.amount = value.floatValue;
                                                        }
                                                    }
                                                }}
                                                displayType={'input'}
                                                thousandSeparator={' '}
                                                suffix={'  ' + harv.combinedData.unitText}
                                                isAllowed={valueMax}
                                                allowNegative={false}
                                                allowedDecimalSeparators={['.', ',']} />
                                        </Form.Group>

                                        {/* Area Amount */}
                                        <Form.Group controlId="formAmount">
                                            <Form.Label className="text-dark">{t('Amount_Area')}</Form.Label>
                                            <NumberFormat
                                                className="form-control inline-form-field"
                                                value={cCD[i].objects[j].combinedData ? cCD[i].objects[j].combinedData.amount * cCD[i].objects[j].combinedArea : 0}
                                                disabled={harv.combinedData.loadAmount && harv.unitNumber === harvestConstants.DRY_CONTENT_UNIT_TYPE}
                                                decimalScale={2}
                                                fixedDecimalScale={false}
                                                onValueChange={(value) => {
                                                    if (value.floatValue && round(value.floatValue) !== round(cCD[i].objects[j].combinedData.amount)) {
                                                        if (value.floatValue) {
                                                            setCCD(updateCalculativeArray(cCD, value.floatValue, i, j));
                                                            harv.combinedData.amount = value.floatValue / harv.combinedArea;
                                                        }
                                                    }
                                                }}
                                                displayType={'input'}
                                                thousandSeparator={' '}
                                                suffix={'  ' + harv.combinedData.unitText}
                                                isAllowed={valueMax}
                                                allowNegative={false}
                                                allowedDecimalSeparators={['.', ',']} />
                                        </Form.Group>

                                        {/* Storage */}
                                        <Form.Group controlId="storage">
                                            <Form.Label className="text-dark">{t('Yield_Storage')}</Form.Label>
                                            <Select
                                                className="text-dark inline-form-field mark-storage"
                                                //disabled={!storagesIsLoading && selectedStorage && selectedStorage.id ? false : true}
                                                value={harv.combinedData.storageId ? harv.storages.find(s => s.value === harv.combinedData.storageId) : { label: t('NoStorages'), value: null }}
                                                onChange={(value) => {
                                                    checkSelectedStorage(value, combinedHarvests, harv);
                                                    harv.combinedData.storageId = value.id;

                                                    let newArr = [...cCD];
                                                    newArr[i].objects[j].combinedData.storageId = value.id;
                                                    setCCD(newArr);
                                                }}
                                                options={harv.storages}
                                                styles={basicStyles}
                                                noOptionsMessage={({ inputValue }) => !inputValue ? t('NoStorages') : ''}
                                            />
                                        </Form.Group>

                                    </div>

                                    { /* ============================================================================== */ }


                                    <div className="mark-row-content">
                                        <Form.Group controlId="fromDate">
                                            <Form.Label className="text-dark">{t('Date')}</Form.Label>
                                            <Form.Control className="form-control inline-form-field" type="text" onClick={() => {
                                                setDate(harv.combinedData);
                                                setCalendarShow(true);
                                            }}
                                                placeholder={formatPlannedDate(harv.combinedData.plannedDate, t)}
                                                value={harv.combinedData.beginDate ? dayJsFormatDate(harv.combinedData.beginDate) : ''}
                                                onChange={() => { }} />
                                        </Form.Group>
                                    </div>
                                </div>)
                        ) : <div>{infoText}</div>}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="modal-footer-right-align">
                    <Button variant="outline-secondary" onClick={props.onHide}>{t('Cancel')}</Button>
                    <Button variant="success" disabled={saveInProgress} className="btn-show-more" onClick={() => { saveHarvests(); }}>{saveInProgress ? <Spinner as="span" animation="border" size="sm" role="status" variant="secondary" /> : t('Save')}</Button>
                </Modal.Footer>
            </Modal>
            {calendarShow === true ?
                <CalendarModal
                    show={calendarShow}
                    onHide={() => setCalendarShow(false)}
                    onChange={updateDate}
                    selecteddate={date.date}
                />
                : ''}
        </>
    )
}

export default MarkHarvestModal;